import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ArsContext } from '../Contexts/ArsContext'
import { NO_CATEGORIES_AVAILABLE_MESSAGE, NO_CATEGORY_FOUND_SEARCH_MESSAGE, SELECT_CATEGORIES, SELECTED_CATEGORIES, VIEW_LESS, VIEW_MORE } from '../ARSConstants'
import CategoryItem from './CategoryItem'
import { SearchIcon } from 'assests/GmsIcons'
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton'
import { TSubmodel } from '../Types'

type TargetCategorySelectorProps = {
    title: string
    categories: TCategory[]
    selectedCategories: TCategory[]
    submodel: TSubmodel
    onToggleChange?: (isEnabled: boolean) => void
}

const TargetCategorySelector = ({
    submodel,
    title,
    categories,
    selectedCategories,
    onToggleChange
}: TargetCategorySelectorProps) => {

    const [searchString, setSearchString] = useState<string>("");
    const [selectedCategoryTypeId, setSelectedCategoryTypeId] = useState<string>("");
    const [viewMoreCategories, setViewMoreCategories] = useState<boolean>(false);
    const [disabled, setDisabled] = useState<boolean>(true);
    const { model, setModel, allCategoryTypes, numberOfCategoriesToShow } = useContext(ArsContext);

    useEffect(() => {
        setDisabled(selectedCategories?.length ? false : true);
    }, [selectedCategories?.length]);

    const addToSelection = (categoryId: string) => {
        if (!model || !submodel) return;

        setModel(prevModel => {
            if (!prevModel) return null;

            const submodelIndex = prevModel.submodel.findIndex(
                sm => sm.submodel_id === submodel.submodel_id
            );

            if (submodelIndex === -1) return prevModel;

            const updatedModel = { ...prevModel };
            const updatedSubmodels = [...updatedModel.submodel];
            const updatedSubmodel = { ...updatedSubmodels[submodelIndex] };

            const targetCategories = updatedSubmodel.target_categories || [];

            if (!targetCategories.includes(categoryId)) {
                updatedSubmodel.target_categories = [...targetCategories, categoryId];
                updatedSubmodels[submodelIndex] = updatedSubmodel;
                updatedModel.submodel = updatedSubmodels;
            }

            return updatedModel;
        });
    };


    const removeFromSelection = (categoryId: string) => {
        if (!model || !submodel) return;

        setModel(prevModel => {
            if (!prevModel) return null;

            const submodelIndex = prevModel.submodel.findIndex(
                sm => sm.submodel_id === submodel.submodel_id
            );

            if (submodelIndex === -1) return prevModel;

            const updatedModel = { ...prevModel };
            const updatedSubmodels = [...updatedModel.submodel];
            const updatedSubmodel = { ...updatedSubmodels[submodelIndex] };

            if (updatedSubmodel.target_categories?.length) {
                updatedSubmodel.target_categories = updatedSubmodel.target_categories.filter(
                    id => id !== categoryId
                );
                updatedSubmodels[submodelIndex] = updatedSubmodel;
                updatedModel.submodel = updatedSubmodels;
            }

            return updatedModel;
        });
    };

    const clearSelection = () => {
        if (!model || !submodel) return;

        setModel(prevModel => {
            if (!prevModel) return null;

            const submodelIndex = prevModel.submodel.findIndex(
                sm => sm.submodel_id === submodel.submodel_id
            );

            if (submodelIndex === -1) return prevModel;

            const updatedModel = { ...prevModel };
            const updatedSubmodels = [...updatedModel.submodel];
            const updatedSubmodel = { ...updatedSubmodels[submodelIndex] };

            updatedSubmodel.target_categories = [];
            updatedSubmodels[submodelIndex] = updatedSubmodel;
            updatedModel.submodel = updatedSubmodels;

            return updatedModel;
        });
    };

    const selectAll = () => {
        if (!model || !submodel || !categories?.length) return;

        setModel(prevModel => {
            if (!prevModel) return null;

            const submodelIndex = prevModel.submodel.findIndex(
                sm => sm.submodel_id === submodel.submodel_id
            );

            if (submodelIndex === -1) return prevModel;

            const updatedModel = { ...prevModel };
            const updatedSubmodels = [...updatedModel.submodel];
            const updatedSubmodel = { ...updatedSubmodels[submodelIndex] };

            const allCategoryIds = categories.map(category => category.id);

            updatedSubmodel.target_categories = allCategoryIds;
            updatedSubmodels[submodelIndex] = updatedSubmodel;
            updatedModel.submodel = updatedSubmodels;

            return updatedModel;
        });
    };

    const filterCategories = (categoryTypeId: string) => {
        setSelectedCategoryTypeId(categoryTypeId);
    };

    const handleSearch = (value: string) => {
        setSearchString(value);
    }

    const filteredCategories = useMemo(() => {
        if (!categories?.length) return [];

        const searchLower = searchString.toLowerCase();

        const selectedCategoryIds = new Set(selectedCategories.map(cat => cat.id));

        return categories.filter(category => {

            if (selectedCategoryIds.has(category.id)) {
                return false;
            }

            if (selectedCategoryTypeId && category.cattype !== selectedCategoryTypeId) {
                return false;
            }

            if (searchString) {
                const name = category.name?.toLowerCase() || '';
                const description = category.description?.toLowerCase() || '';

                if (!name.includes(searchLower) && !description.includes(searchLower)) {
                    return false;
                }
            }

            return true;
        });
    }, [
        categories,
        selectedCategoryTypeId,
        searchString,
        selectedCategories,
    ]);

    const handleEnableToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isEnabled = e.target.checked;
        setDisabled(!isEnabled);

        if (!isEnabled) {
            clearSelection();
        }

        if (onToggleChange) {
            onToggleChange(isEnabled);
        }
    };

    return (
        <div className="tw-w-full tw-ps-2 tw-pt-2">
            <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-p-4 tw-text-left tw-bg-white tw-rounded-t tw-border tw-border-[#c9cccf]">
                    <span className="tw-text-lg tw-font-semibold">{title}</span>
                    {<ToggleButton
                        name={`toggle-button`}
                        value={!disabled}
                        onChange={handleEnableToggle}
                    />}
                </div>
                {!disabled ? <div className=" tw-h-auto tw-bg-[#f4f5f7] tw-rounded-bl tw-rounded-br ">
                    <div className="tw-p-4 tw-bg-gray-50 tw-border tw-border-[#c9cccf]">
                        <div className="tw-mb-3 tw-flex tw-justify-between tw-items-center">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <span className="tw-text-lg tw-font-medium">{SELECTED_CATEGORIES}</span>
                                <span className="tw-inline-flex tw-items-center tw-justify-center tw-h-5 tw-w-5 tw-rounded-full tw-bg-neutral-500 tw-text-white tw-text-xs">
                                    {selectedCategories.length}
                                </span>
                            </div>
                        </div>

                        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                            {selectedCategories.map((item: TCategory) => (
                                <CategoryItem
                                    key={item?.id}
                                    item={item}
                                    isSelected={true}
                                    handleClick={removeFromSelection}
                                    disabledClick={false}
                                />
                            ))}

                            {selectedCategories.length > 0 && (
                                <div
                                    className="tw-text-blue-800 tw-text-xs tw-cursor-pointer hover:tw-underline tw-ml-1"
                                    onClick={clearSelection}
                                >
                                    Clear All
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="tw-p-4 tw-bg-white tw-border tw-border-[#c9cccf]">
                        <div className="tw-mb-3 tw-flex tw-justify-between tw-items-center">
                            <span className="tw-text-lg tw-font-medium">{SELECT_CATEGORIES}</span>
                            <div className="tw-flex tw-space-x-2 tw-items-center">
                                <select
                                    className="tw-w-48 tw-max-w-48 tw-px-2 tw-h-8 tw-bg-gray-100 tw-rounded tw-border tw-border-[#c9c9cf] tw-text-ellipsis tw-overflow-hidden"
                                    onChange={(e) => filterCategories(e.target.value)}
                                    value={selectedCategoryTypeId}
                                >
                                    <option value="">Select category type</option>
                                    {allCategoryTypes?.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </select>
                                <div className="tw-relative">
                                    <input
                                        type="text"
                                        className="tw-px-2 tw-h-8 tw-bg-white tw-rounded tw-border tw-border-[#c9c9cf]"
                                        placeholder="Search Category"
                                        value={searchString}
                                        onChange={(e) => handleSearch(e.target.value)}
                                    />
                                    <span
                                        className="tw-absolute tw-inset-y-0 tw-right-0 tw-px-3 tw-py-1.5 tw-flex tw-items-center tw-justify-center tw-text-gray-400"
                                        style={{ pointerEvents: "none" }}
                                    >
                                        <SearchIcon />
                                    </span>
                                </div>
                                {categories && <div className="tw-justify-start tw-pl-3 tw-text-blue-800 tw-text-xs tw-cursor-pointer" onClick={selectAll}>Select All</div>}
                            </div>
                        </div>

                        <div className="tw-flex tw-flex-wrap">
                            {filteredCategories && filteredCategories.length > 0 ? (
                                filteredCategories
                                    .slice(
                                        0,
                                        viewMoreCategories || searchString !== ""
                                            ? filteredCategories.length
                                            : Math.min(numberOfCategoriesToShow, filteredCategories.length)
                                    )
                                    .map((item: TCategory) => {
                                        return <CategoryItem key={item?.id} item={item} isSelected={false} handleClick={addToSelection} disabledClick={false} />
                                    })
                            ) : searchString !== "" ? (
                                <div className="tw-text-gray-500">
                                    {NO_CATEGORY_FOUND_SEARCH_MESSAGE}
                                </div>
                            ) : (
                                <div className="tw-text-gray-500">{NO_CATEGORIES_AVAILABLE_MESSAGE}</div>
                            )}

                            {searchString === "" &&
                                filteredCategories?.length > numberOfCategoriesToShow && (
                                    <button
                                        onClick={() => setViewMoreCategories(!viewMoreCategories)}
                                        className="tw-text-blue-500 hover:tw-text-blue-700 tw-ml-2"
                                    >
                                        {viewMoreCategories ? VIEW_LESS : VIEW_MORE}
                                    </button>
                                )}
                        </div>
                    </div>
                </div> : null}
            </div>
        </div >
    );
}

export default TargetCategorySelector